import React, { useState, useEffect, useCallback } from 'react';
import { fetchTeknikServisStokV2 } from '../services/stokV2Service';
import StokParcaSiparisGecmisiModal from './stok/StokParcaSiparisGecmisiModal';
import StokOzMaliyetModal from './stok/StokOzMaliyetModal';
import StokOzetKart from './stok/StokOzetKart';
import KonsinyeOzDonusumModal from './stok/KonsinyeOzDonusumModal';
import TableListFooter from './TableListFooter';
import { normalizeSayfalama } from '../utils/pagination';
import { toValidObjectIdString } from '../utils/security';
import './TeknikServisStokDetayPanel.css';

const PAGE_SIZE = 25;

const adetStyle = (adet) => {
  const base = { textAlign: 'center', fontWeight: 600 };
  if (adet < 0) return { ...base, color: 'var(--danger, #e74c3c)' };
  if (adet === 0) return { ...base, color: 'var(--text-light, #999)' };
  return base;
};

const renderKatmanlarAltSatir = (row) => {
  const ozKatmanlar = row?.ozKatmanlar?.length
    ? row.ozKatmanlar
    : (row?.katmanlar || []).filter((k) => k.stokTipi !== 'konsinye' && Number(k.birimMaliyet) > 0);
  if (!ozKatmanlar.length) return null;
  return (
    <div style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '4px', lineHeight: 1.45 }}>
      {ozKatmanlar.map((k, i) => (
        <span key={k.stokKatmanId || i} style={{ display: 'inline-block', marginRight: '10px' }}>
          {Number(k.birimMaliyet).toFixed(2)} ₺ × {k.toplam ?? k.kalanAdet ?? 0}
          {(k.rezerveAdet || 0) > 0 ? ` (${k.rezerveAdet} iade rez.)` : ''}
        </span>
      ))}
    </div>
  );
};

/**
 * Teknik servise ait stok detayı: güncel/arşiv, arama, sayfalama, işlem modalları.
 */
const TeknikServisStokDetayPanel = ({
  teknikServisId,
  teknikServisAdi,
  teknikServisKod,
  embedded = false,
  active = true,
  showServiceHeader = false,
  showModalHeader = false,
  onClose,
}) => {
  const validId = toValidObjectIdString(teknikServisId)
    || (typeof teknikServisId === 'string' && /^[0-9a-fA-F]{24}$/.test(teknikServisId) ? teknikServisId : null);

  const [stokTab, setStokTab] = useState('guncel');
  const [arama, setArama] = useState('');
  const [debouncedArama, setDebouncedArama] = useState('');
  const [stokListesi, setStokListesi] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, totalPages: 1 });
  const [gecmisParca, setGecmisParca] = useState(null);
  const [maliyetParca, setMaliyetParca] = useState(null);
  const [donusumParca, setDonusumParca] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedArama(arama.trim()), 400);
    return () => clearTimeout(t);
  }, [arama]);

  useEffect(() => {
    setCurrentPage(1);
  }, [validId, stokTab, debouncedArama]);

  const loadStok = useCallback(async () => {
    if (!validId || !active) return;
    setLoading(true);
    try {
      const params = { sayfa: currentPage, limit: PAGE_SIZE, tab: stokTab };
      if (debouncedArama) params.q = debouncedArama;
      const response = await fetchTeknikServisStokV2(validId, params);
      if (response?.basarili && response?.veri) {
        setStokListesi(response.veri.stokListesi || []);
        const meta = normalizeSayfalama(response.veri.sayfalama, PAGE_SIZE);
        setPaginationMeta({ total: meta.total, totalPages: meta.totalPages });
      } else {
        setStokListesi([]);
        setPaginationMeta({ total: 0, totalPages: 1 });
      }
    } catch {
      setStokListesi([]);
      setPaginationMeta({ total: 0, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  }, [validId, active, debouncedArama, stokTab, currentPage]);

  useEffect(() => {
    loadStok();
  }, [loadStok]);

  const emptyMessage = debouncedArama
    ? 'Aramanızla eşleşen parça bulunamadı.'
    : stokTab === 'arsiv'
      ? 'Arşivde parça bulunamadı.'
      : 'Güncel stok yok. Geçmiş hareket için parça adı veya kodu ile arayın.';

  if (!validId) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-gray)' }}>
        Geçersiz teknik servis.
      </div>
    );
  }

  const panelClass = [
    'teknik-servis-stok-detay-panel',
    embedded ? 'teknik-servis-stok-detay-panel--embedded' : '',
    showModalHeader ? 'teknik-servis-stok-detay-panel--modal' : '',
  ].filter(Boolean).join(' ');

  const content = (
    <>
      {showModalHeader && (
        <div className="modal-header stok-detay-modal__header">
          <div className="stok-detay-modal__title-group">
            <div className="stok-detay-modal__icon-wrapper">
              <i className="fas fa-warehouse" aria-hidden="true" />
            </div>
            <div>
              <h2>{teknikServisAdi || 'Teknik Servis'}</h2>
              <span className="stok-detay-modal__subtitle">
                {teknikServisKod ? `Kod: ${teknikServisKod}` : ''}
                {teknikServisKod ? ' · ' : ''}
                {debouncedArama ? 'Arama' : stokTab === 'arsiv' ? 'Arşiv' : 'Güncel'}
              </span>
            </div>
          </div>
          {onClose ? (
            <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      )}

      {showServiceHeader && !showModalHeader && (
        <div className="teknik-servis-stok-detay-panel__embedded-header">
          <div>
            <div className="section-title" style={{ marginBottom: 4 }}>Stok</div>
            <p style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0, lineHeight: 1.5 }}>
              {teknikServisAdi || 'Teknik Servis'}
              {teknikServisKod ? ` · Kod: ${teknikServisKod}` : ''}
            </p>
          </div>
        </div>
      )}

      <div className="teknik-servis-stok-detay-panel__controls">
        <div className={`stok-detay-modal__tabs ${embedded ? 'teknik-servis-stok-detay-panel__tabs-embedded' : ''}`}>
          <div className="tabs" style={{ marginBottom: 0 }}>
            <button
              type="button"
              className={`tab-btn ${stokTab === 'guncel' ? 'active' : ''}`}
              onClick={() => setStokTab('guncel')}
            >
              Güncel
            </button>
            <button
              type="button"
              className={`tab-btn ${stokTab === 'arsiv' ? 'active' : ''}`}
              onClick={() => setStokTab('arsiv')}
            >
              Arşiv
            </button>
          </div>
        </div>

        <div className="teknik-servis-stok-detay-panel__toolbar">
          <div className="search-box teknik-servis-stok-detay-panel__search">
            <i className="fas fa-search" aria-hidden="true" />
            <input
              type="text"
              placeholder="Parça adı veya kodu ile ara..."
              value={arama}
              onChange={(e) => setArama(e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>

        {!loading && !debouncedArama && stokListesi.length > 0 && (
          <StokOzetKart
            parcalar={stokListesi}
            className={showModalHeader ? 'stok-ozet-kart--modal-header' : 'stok-ozet-kart--embedded'}
          />
        )}
      </div>

      <div
        className={[
          'stok-detay-modal__body',
          showModalHeader ? 'modal-body' : '',
          embedded ? 'teknik-servis-stok-detay-panel__body-embedded' : '',
        ].filter(Boolean).join(' ')}
      >
        {loading ? (
          <div className="stok-detay-modal__loading">
            <div className="spinner-sm" />
            <span>Stok verileri yükleniyor...</span>
          </div>
        ) : stokListesi.length === 0 ? (
          <div className="stok-detay-modal__empty">
            <i className="fas fa-box-open" aria-hidden="true" />
            <p>{emptyMessage}</p>
          </div>
        ) : (
          <>
            <div className="stok-detay-modal__table-wrap">
              <table className="admins-table stok-detay-modal__table">
                <colgroup>
                  <col className="col-parca-adi" style={{ width: '30%' }} />
                  <col className="col-parca-kodu" style={{ width: '22%' }} />
                  <col className="col-oz" style={{ width: '9%' }} />
                  <col className="col-konsinye" style={{ width: '9%' }} />
                  <col className="col-adet" style={{ width: '8%' }} />
                  <col className="col-islem" style={{ width: '22%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="stok-detay-modal__col-parca-adi">Parça Adı</th>
                    <th>Parça Kodu</th>
                    <th>Öz stok</th>
                    <th>Konsinye</th>
                    <th>Toplam</th>
                    <th className="islemler-sutunu stok-detay-modal__actions-col">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {stokListesi.map((row, idx) => (
                    <tr
                      key={row.icerikId || idx}
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (!validId || !row.icerikId) return;
                        setMaliyetParca({
                          teknikServisId: validId,
                          icerikId: row.icerikId,
                          parcaAdi: row.parcaAdi,
                          parcaKodu: row.parcaKodu,
                        });
                      }}
                      title="Öz stok maliyet dağılımını görmek için tıklayın"
                    >
                      <td title={row.parcaAdi || ''}>
                        <div style={{ fontWeight: 500 }}>{row.parcaAdi || '—'}</div>
                        {renderKatmanlarAltSatir(row)}
                      </td>
                      <td title={row.parcaKodu || ''}>
                        <code className="stok-detay-modal__code">{row.parcaKodu || '—'}</code>
                      </td>
                      <td style={adetStyle(row.ozAdet ?? 0)}>{row.ozAdet ?? 0}</td>
                      <td style={adetStyle(row.konsinyeAdet ?? 0)}>{row.konsinyeAdet ?? 0}</td>
                      <td style={adetStyle(row.adet ?? 0)}>{row.adet ?? 0}</td>
                      <td
                        className="islemler-sutunu stok-detay-modal__actions-col"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {(row.konsinyeAdet ?? 0) > 0 ? (
                          <button
                            type="button"
                            className="category-action-btn edit-btn"
                            title="Konsinyeyi öz stoka çevir"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!validId || !row.icerikId) return;
                              setDonusumParca({
                                teknikServisId: validId,
                                icerikId: row.icerikId,
                                parcaAdi: row.parcaAdi,
                                parcaKodu: row.parcaKodu,
                                maxKonsinye: row.konsinyeAdet ?? 0,
                                netFiyat: row.netFiyat,
                              });
                            }}
                          >
                            <i className="fas fa-exchange-alt" aria-hidden="true" />
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="category-action-btn edit-btn"
                          title="Stok geçmişi"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!validId || !row.icerikId) return;
                            setGecmisParca({
                              teknikServisId: validId,
                              icerikId: row.icerikId,
                              parcaAdi: row.parcaAdi,
                              parcaKodu: row.parcaKodu,
                              mevcutAdet: row.adet ?? 0,
                              ozAdet: row.ozAdet ?? 0,
                            });
                          }}
                        >
                          <i className="fas fa-history" aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TableListFooter
              totalRecords={paginationMeta.total}
              currentPage={currentPage}
              totalPages={paginationMeta.totalPages || 1}
              onPageChange={setCurrentPage}
              loading={loading}
              embedded
            />
          </>
        )}
      </div>

      {showModalHeader && onClose && !loading && stokListesi.length > 0 ? (
        <div className="modal-footer stok-detay-modal__footer">
          <button type="button" className="secondary-btn" onClick={onClose}>
            Kapat
          </button>
        </div>
      ) : null}

      <KonsinyeOzDonusumModal
        open={!!donusumParca}
        onClose={() => setDonusumParca(null)}
        teknikServisId={donusumParca?.teknikServisId}
        icerikId={donusumParca?.icerikId}
        parcaAdi={donusumParca?.parcaAdi}
        parcaKodu={donusumParca?.parcaKodu}
        maxKonsinye={donusumParca?.maxKonsinye ?? 0}
        netFiyat={donusumParca?.netFiyat}
        onSuccess={loadStok}
      />

      <StokOzMaliyetModal
        open={!!maliyetParca}
        onClose={() => setMaliyetParca(null)}
        teknikServisId={maliyetParca?.teknikServisId}
        icerikId={maliyetParca?.icerikId}
        parcaAdi={maliyetParca?.parcaAdi}
        parcaKodu={maliyetParca?.parcaKodu}
      />

      <StokParcaSiparisGecmisiModal
        open={!!gecmisParca}
        onClose={() => setGecmisParca(null)}
        mode="admin"
        teknikServisId={gecmisParca?.teknikServisId}
        icerikId={gecmisParca?.icerikId}
        parcaAdi={gecmisParca?.parcaAdi}
        parcaKodu={gecmisParca?.parcaKodu}
        mevcutAdet={gecmisParca?.mevcutAdet}
        ozAdet={gecmisParca?.ozAdet}
      />
    </>
  );

  if (embedded) {
    return <div className={panelClass}>{content}</div>;
  }

  return <div className={panelClass}>{content}</div>;
};

export default TeknikServisStokDetayPanel;
